import {
  Button,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import ShareIcon from "@mui/icons-material/Share";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DownloadIcon from "@mui/icons-material/Download";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AdminSessionViewDto } from "@swagger/typescript-fetch-goflyer";
import { messages } from "../../../../locales/messages";
import { LeftButtonSection, RightActionSection } from "../styled";
import { ReviewPresenceEntry } from "../types";
import { ConnectionDot } from "./ConnectionDot";
import { PresenceBadge } from "./PresenceBadge";

interface Props {
  session?: AdminSessionViewDto;
  onShare: () => void;
  onOpenFlyerComments: () => void;
  onApprove: () => void;
  onReopen: () => void;
  onExportReport: () => void;
  connected: boolean;
  presence: ReviewPresenceEntry[];
  bootstrapLoading?: boolean;
}

export function ReviewCommandBar({
  session,
  onShare,
  onOpenFlyerComments,
  onApprove,
  onReopen,
  onExportReport,
  connected,
  presence,
  bootstrapLoading,
}: Props) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const isApproved =
    session?.status === AdminSessionViewDto.StatusEnum.APPROVED;

  const closeMenu = () => setMenuAnchor(null);

  return (
    <>
      <LeftButtonSection>
        <ConnectionDot connected={connected} />
        <Typography
          variant="h6"
          component="h1"
          sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
        >
          {t(messages.Flyer_Review())}
        </Typography>
      </LeftButtonSection>
      <RightActionSection>
        <PresenceBadge presence={presence} />
        {isApproved ? (
          <Button
            size="small"
            variant="outlined"
            startIcon={<LockOpenIcon sx={{ fontSize: 16 }} />}
            color="warning"
            onClick={onReopen}
            disabled={bootstrapLoading}
            data-testid="reopen-button"
          >
            {t(messages.Reopen())}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={onApprove}
            disabled={bootstrapLoading}
            data-testid="approve-button"
          >
            {t(messages.Approve())}
          </Button>
        )}
        <IconButton
          aria-label={t(messages.Review_More_Actions())}
          data-testid="review-more-button"
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          disabled={bootstrapLoading}
          size="small"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={closeMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem
            data-testid="share-button"
            onClick={() => {
              closeMenu();
              onShare();
            }}
          >
            <ListItemIcon>
              <ShareIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t(messages.Share())}</ListItemText>
          </MenuItem>
          <MenuItem
            data-testid="flyer-comments-button"
            onClick={() => {
              closeMenu();
              onOpenFlyerComments();
            }}
          >
            <ListItemIcon>
              <ChatBubbleOutlineIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t(messages.Flyer_Comments())}</ListItemText>
          </MenuItem>
          <MenuItem
            data-testid="export-report-button"
            onClick={() => {
              closeMenu();
              onExportReport();
            }}
          >
            <ListItemIcon>
              <DownloadIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t(messages.Export_Change_Report())}</ListItemText>
          </MenuItem>
        </Menu>
      </RightActionSection>
    </>
  );
}
